import { AbstractAttrDescriptor, FormControlType } from '../base-entity/abstact-attr.descriptor';
import { BaseEntityAttrDescriptor } from '../base-entity/base-entity-attr.descriptor';
import { DEFAULT_DATE_TIME_FORMAT, effectiveDateFormat, formatDateValue } from '../base-entity/date-format';
import { commentsOf } from '../base-form/comments/entity-comment';
import { formatAddress } from '../base-form/address/address-formats';
import { countryName } from '../base-form/address/countries';
import { addressOf } from '../base-form/address/postal-address';
import { formatGeoLocation } from '../base-form/geo-location/geo-location';
import { filterAttributeDescriptors } from '../base-entity/filter-attr-descriptor';
import type { PdfColumnDefinition } from './pdf-export.types';

/**
 * Converts a BaseEntityDescriptor's attribute descriptors into PdfColumnDefinition[]
 * for {@link PdfExportService}.
 *
 * The set of columns mirrors exactly what BaseEntityListComponent renders in its table:
 * descriptors are flattened via {@link filterAttributeDescriptors} (so nested FlexBox
 * attributes are included) and any attribute with `hideInTable === true` is dropped — the
 * same rule the list uses. An attribute hidden from the list is therefore also absent from
 * the PDF.
 *
 * Type-appropriate alignment and value formatters are derived from `formControlType` so
 * booleans, dates, tags and artifacts render sensibly without per-field configuration. A date is written in
 * its attribute's `dateFormat` and `locale` — the active language, as the list shows it — not the browser's.
 */
export function entityDescriptorToPdfColumns(descriptors: AbstractAttrDescriptor[], locale?: string): PdfColumnDefinition[] {
  return filterAttributeDescriptors(descriptors)
    .filter((descriptor) => descriptor.hideInTable !== true)
    .map((descriptor) => ({
      field: descriptor.attrName,
      header: descriptor.label,
      align: columnAlign(descriptor.formControlType),
      formatter: columnFormatter(descriptor, locale),
    }));
}

function columnAlign(type: FormControlType): PdfColumnDefinition['align'] {
  return type === FormControlType.CHECKBOX ? 'center' : 'left';
}

function columnFormatter(descriptor: BaseEntityAttrDescriptor, locale: string | undefined): PdfColumnDefinition['formatter'] | undefined {
  switch (descriptor.formControlType) {
    case FormControlType.CHECKBOX:
      return formatBoolean;

    case FormControlType.DATE:
      return (value) => formatDate(value, descriptor, locale);

    case FormControlType.TAGS:
      return formatTags;

    case FormControlType.ARTIFACT:
    case FormControlType.AVATAR:
      return formatArtifact;

    case FormControlType.COMMENTS:
      return (value) => formatComments(value, locale);

    case FormControlType.ADDRESS:
      return (value) => formatAddress(addressOf(value), (code) => countryName(code, locale ?? navigator.language));

    case FormControlType.GEO_LOCATION:
      return (value) => formatGeoLocation(value);

    // TEXT_BOX, TEXTAREA, DROPDOWN, FOREIGN_KEY, LOOKUP, etc. → default string coercion in the service
    default:
      return undefined;
  }
}

function formatBoolean(value: unknown): string {
  if (value === true || value === 'true') return '✓';
  if (value === false || value === 'false') return '✗';
  return '';
}

function formatDate(value: unknown, descriptor: BaseEntityAttrDescriptor, locale: string | undefined): string {
  if (!value) return '';
  return toText(formatDateValue(value, effectiveDateFormat(descriptor), locale ?? navigator.language));
}

function formatTags(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  return toText(value);
}

/** The history's size and its latest entry — the comments themselves are rich text, not a table cell. */
function formatComments(value: unknown, locale: string | undefined): string {
  const comments = commentsOf(value);
  const latest = comments.at(-1);
  if (!latest) return '';
  const when = toText(formatDateValue(latest.createdAt, DEFAULT_DATE_TIME_FORMAT, locale ?? navigator.language));
  return `${comments.length} (${[latest.author, when].filter(Boolean).join(', ')})`;
}

/** A multi-valued attribute holds an array; its names are joined. */
function formatArtifact(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(formatArtifact).filter(Boolean).join(', ');
  const artifact = value as { name?: string; objectId?: string };
  return artifact.name ?? artifact.objectId ?? '';
}

/** Coerces an arbitrary value to a cell string without ever relying on Object's default `[object Object]` stringification. */
function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return value.toString();
  if (typeof value === 'object') return JSON.stringify(value);
  return ''; // symbol / function — not meaningful in a table cell
}
